<script setup lang="ts">
import { VIconBtn } from 'vuetify/labs/VIconBtn'
import JournalEntryModal from '../modals/JournalEntryModal.vue'
import JournalHeaderFilter from '../JournalHeaderFilter.vue'
import JournalDetailHeader from './JournalDetailHeader.vue'
import { useJournalDetailPage } from '@/modules/journals'
import { TABLE_ITEMS_PER_PAGE_OPTIONS } from '@/views/apps/journals/constants/pagination'

const {
  activeFilterChips,
  applyEntryFiltersAndFetch,
  canCreate,
  createEntry,
  currentJournal,
  entrySearchQuery,
  entrySort,
  formatAttributeValue,
  formatPersonName,
  getEntryFilter,
  getFileNameFromPath,
  getFileUrl,
  getSortIcon,
  handleSort,
  hasActiveFilters,
  isEntryModalOpen,
  isFetchingAttributes,
  isFetchingEntries,
  isFilterActive,
  itemsPerPage,
  journalId,
  journalName,
  onEntriesOptionsUpdate,
  onEntryRowClick,
  onEntrySaved,
  page,
  processedEntries,
  removeFilterChip,
  resetAllFilters,
  selectedEntryId,
  setEntryFilter,
  tableHeaders,
  totalEntriesCount,
  visibleAttributes,
} = useJournalDetailPage()
</script>

<template>
  <div class="journal-detail">
    <div>
      <JournalDetailHeader
        v-model:search-query="entrySearchQuery"
        :journal-id="journalId"
        :journal-name="journalName"
        :can-create="canCreate"
        @create="createEntry"
      />
    </div>

    <div class="mb-4">
      <div
        v-if="currentJournal"
        class="journal-info d-flex flex-column"
      >
        <div
          v-if="currentJournal.description"
          class="journal-info-item"
        >
          <span class="journal-info-value text-base">{{
            currentJournal.description
          }}</span>
        </div>
        <div class="d-flex journal-info-items ga-3 mb-4">
          <div
            v-if="currentJournal.departments?.length"
            class="journal-info-item"
          >
            <span class="journal-info-label d-flex align-center gap-1">
              <i class="ri-building-4-line v-icon notranslate v-theme--light" />
              Отделение:
            </span>
            <span class="journal-info-value">{{
              currentJournal.departments
                .map((d) => d.short_name || d.name)
                .join(", ")
            }}</span>
          </div>
          <div
            v-if="currentJournal.responsible_employees?.length"
            class="journal-info-item"
          >
            <span class="journal-info-label d-flex align-center gap-1">
              <i class="ri-user-3-line v-icon notranslate v-theme--light" />
              Ответственный:
            </span>
            <span class="journal-info-value">{{
              currentJournal.responsible_employees
                .map((emp) => formatPersonName(emp))
                .join(", ")
            }}</span>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="hasActiveFilters"
      class="filter-toolbar d-flex align-center flex-wrap gap-2 mb-3"
    >
      <VBtn
        size="small"
        variant="outlined"
        color="secondary"
        prepend-icon="ri-filter-off-line"
        @click="resetAllFilters"
      >
        Сбросить фильтры
      </VBtn>
      <VChip
        v-for="chip in activeFilterChips"
        :key="chip.attrKey"
        closable
        size="small"
        @click:close="removeFilterChip(chip.attrKey)"
      >
        {{ chip.label }}
      </VChip>
    </div>

    <div class="wrapper">
      <VDataTableServer
        :key="`table-${entrySort}`"
        v-model:page="page"
        v-model:items-per-page="itemsPerPage"
        :loading="isFetchingEntries || isFetchingAttributes"
        :headers="tableHeaders"
        :items="processedEntries"
        :items-length="totalEntriesCount ?? 0"
        item-value="id"
        class="custom-data-table"
        :multi-sort="true"
        @update:options="onEntriesOptionsUpdate"
        @click:row="onEntryRowClick"
      >
        <template #no-data>
          <div class="pa-6 text-medium-emphasis">
            <div v-if="isFetchingEntries || isFetchingAttributes">
              Загрузка...
            </div>
            <div v-else>
              Нет данных
            </div>
          </div>
        </template>

        <template
          v-for="attr in visibleAttributes"
          :key="`attribute_${attr.id}`"
          #[`item.attribute_${attr.id}`]="{ item }"
        >
          <div class="attribute-cell">
            <div
              v-if="
                attr.attribute_type === 'file' && item[`attribute_${attr.id}`]
              "
              class="file-cell"
            >
              <a
                v-if="typeof item[`attribute_${attr.id}`] === 'string'"
                :href="getFileUrl(String(item[`attribute_${attr.id}`]))"
                target="_blank"
                class="file-link"
                :title="
                  getFileNameFromPath(String(item[`attribute_${attr.id}`]))
                "
                @click.stop
              >
                <span class="file-name">{{
                  getFileNameFromPath(String(item[`attribute_${attr.id}`]))
                }}</span>
                <VIcon
                  icon="ri-download-line"
                  size="small"
                  class="file-icon"
                />
              </a>
              <span
                v-else
                class="attribute-value"
              >
                {{
                  formatAttributeValue(
                    item[`attribute_${attr.id}`],
                    attr.attribute_type,
                  ) || "—"
                }}
              </span>
            </div>
            <div
              v-else
              class="attribute-value"
            >
              {{
                formatAttributeValue(
                  item[`attribute_${attr.id}`],
                  attr.attribute_type,
                ) || "—"
              }}
            </div>
          </div>
        </template>

        <template #item.number="{ item }">
          <div class="attribute-cell">
            <div class="attribute-value">
              {{ item.number ?? "—" }}
            </div>
          </div>
        </template>

        <template #header.number="{ column }">
          <div class="header-with-filter">
            <span>{{ column.title }}</span>
            <VIconBtn
              variant="plain"
              :icon="getSortIcon('number')"
              color="default"
              size="small"
              @click="handleSort('number')"
            />
          </div>
        </template>

        <template
          v-for="attr in visibleAttributes"
          :key="`header_attribute_${attr.id}`"
          #[`header.attribute_${attr.id}`]="{ column }"
        >
          <div class="header-with-filter">
            <span>{{ column.title }}</span>
            <JournalHeaderFilter
              :attr="attr"
              :journal-id="journalId"
              :model-value="getEntryFilter(attr.id)"
              :active="isFilterActive(getEntryFilter(attr.id))"
              @update:model-value="(v) => setEntryFilter(attr.id, v)"
              @apply="applyEntryFiltersAndFetch"
            />
            <VIconBtn
              variant="plain"
              :icon="getSortIcon(`attribute_${attr.id}`)"
              color="default"
              size="small"
              @click="handleSort(`attribute_${attr.id}`)"
            />
          </div>
        </template>

        <template #bottom>
          <div class="custom-data-table-footer">
            <VDataTableFooter
              show-current-page
              :items-per-page-options="TABLE_ITEMS_PER_PAGE_OPTIONS"
            />
          </div>
        </template>
      </VDataTableServer>
    </div>

    <JournalEntryModal
      v-model:is-open="isEntryModalOpen"
      :journal-id="journalId"
      :entry-id="selectedEntryId"
      @saved="onEntrySaved"
    />
  </div>
</template>

<style scoped lang="scss">
@use '@/modules/journals/styles/dataTable' as dt;

@include dt.custom-data-table;
@include dt.custom-data-table-footer;

.journal-detail {
  height: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-block-size: 0;
}

.attribute-cell {
  min-height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.attribute-value {
  overflow-wrap: break-word;
  text-align: center;
}

:deep(.v-table .v-table__wrapper) {
  border-radius: 0;
  min-height: 280px !important;
}

:deep(.v-field) {
  font-size: 0.875rem;
}

.wrapper {
  box-shadow: 0 4px 10px rgba(46, 38, 61, 0.2);
  background: rgb(var(--v-theme-paper));
  flex: 1;
  min-block-size: 0;
}

:deep(.custom-data-table .v-data-table__th) {
  background-color: rgb(var(--v-theme-primary-100)) !important;
  position: relative;
  padding-inline: 1rem;
  text-align: center;
}

:deep(.custom-data-table .v-data-table__td) {
  padding-inline: 1rem;
  text-align: center;
}

:deep(.custom-data-table td),
:deep(.custom-data-table th) {
  text-align: center;
}

:deep(.custom-data-table tr):hover {
  cursor: pointer;
}

:deep(.header-with-filter) {
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 4px;
}

:deep(.header-with-filter span) {
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: normal;
  text-align: left;
}

.journal-info {
  flex-shrink: 0;
  font-size: 0.875rem;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
}

.journal-info-items {
  flex-wrap: wrap;
}

.journal-info-item {
  display: flex;
  gap: 4px;
}

.journal-info-label {
  font-weight: 500;
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
}

.journal-info-value {
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
}

.file-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.file-link {
  display: flex;
  align-items: center;
  gap: 6px;
  text-decoration: none;
  color: rgb(var(--v-theme-primary));
  max-width: 100%;
  overflow: hidden;
}

.file-link:hover {
  text-decoration: underline;
}

.file-icon {
  flex-shrink: 0;
}

.file-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 200px;
}

:deep(.custom-data-table table) {
  table-layout: auto;
}

:deep(.custom-data-table td) {
  overflow: hidden;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

:deep(.custom-data-table th) {
  overflow: hidden;
}
</style>
